"use client";

import { Button } from "@workspace/ui/components/button";
import { InputGroupButton } from "@workspace/ui/components/input-group";
import { toast } from "@workspace/ui/components/sonner";
import { cn } from "@workspace/ui/lib/utils";
import { Check, Copy } from "lucide-react";
import {
  type ComponentProps,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const COPIED_DURATION_MS = 1800;
const COPY_LABEL = "Copiar";
const COPIED_LABEL = "Copiado";

type CopyButtonContextValue = {
  state: { copied: boolean };
  actions: { copy: () => Promise<void> };
};

const CopyButtonContext = createContext<CopyButtonContextValue | null>(null);

function useCopyButton() {
  const context = useContext(CopyButtonContext);
  if (!context) {
    throw new Error(
      "Las partes de CopyButton deben usarse dentro de CopyButton"
    );
  }
  return context;
}

// Parts are separate named exports, not a namespace object, because server
// components cannot dot into a client module.
export function CopyButton({
  children,
  value,
}: {
  children: ReactNode;
  value: string;
}) {
  const [copiedValue, setCopiedValue] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      toast.error("No se pudo copiar");
      return;
    }

    setCopiedValue(value);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setCopiedValue(null);
      timeoutRef.current = null;
    }, COPIED_DURATION_MS);
  }, [value]);

  // A new value is not copied yet, so the feedback never outlives its value.
  const copied = copiedValue === value;
  const contextValue = useMemo(
    () => ({ state: { copied }, actions: { copy } }),
    [copied, copy]
  );

  return (
    <CopyButtonContext value={contextValue}>
      {/* Before the children so it never becomes a trigger's last sibling. */}
      <span className="sr-only" role="status">
        {copied ? COPIED_LABEL : ""}
      </span>
      {children}
    </CopyButtonContext>
  );
}

export function CopyButtonTrigger({
  onClick,
  ...props
}: ComponentProps<typeof Button>) {
  const { actions } = useCopyButton();

  return (
    <Button
      type="button"
      {...props}
      onClick={(event) => {
        onClick?.(event);
        void actions.copy();
      }}
    />
  );
}

// Explicit input-group variant, so the addon keeps the input-group button styles.
export function CopyButtonInputGroupTrigger({
  onClick,
  ...props
}: ComponentProps<typeof InputGroupButton>) {
  const { actions } = useCopyButton();

  return (
    <InputGroupButton
      {...props}
      onClick={(event) => {
        onClick?.(event);
        void actions.copy();
      }}
    />
  );
}

export function CopyButtonIcon({ className }: { className?: string }) {
  const { state } = useCopyButton();

  return (
    <span
      aria-hidden="true"
      className="t-icon-swap"
      data-state={state.copied ? "check" : "copy"}
    >
      <span className="t-icon" data-icon="copy">
        <Copy className={className} />
      </span>
      <span className="t-icon" data-icon="check">
        <Check className={className} />
      </span>
    </span>
  );
}

function getTextSwapDurationMs() {
  const duration = Number.parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue(
      "--text-swap-dur"
    )
  );
  return Number.isFinite(duration) ? duration : 150;
}

export function CopyButtonLabel() {
  const { state } = useCopyButton();
  const text = state.copied ? COPIED_LABEL : COPY_LABEL;
  const [displayedText, setDisplayedText] = useState(text);
  const [phase, setPhase] = useState<"idle" | "exit" | "enter">("idle");
  const elementRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (text === displayedText) {
      // A click during the exit reverses the swap, so the label shows again.
      setPhase("idle");
      return;
    }

    // Without the transition, the exit would only blank the label.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplayedText(text);
      setPhase("idle");
      return;
    }

    setPhase("exit");
    const timeout = setTimeout(() => {
      setDisplayedText(text);
      setPhase("enter");
    }, getTextSwapDurationMs());

    return () => clearTimeout(timeout);
  }, [displayedText, text]);

  useEffect(() => {
    if (phase !== "enter") {
      return;
    }

    // Flush the enter start position so removing it transitions.
    void elementRef.current?.offsetWidth;
    const frame = requestAnimationFrame(() => setPhase("idle"));

    return () => cancelAnimationFrame(frame);
  }, [phase]);

  return (
    <span className="inline-grid">
      {/* Reserves the width of the longest label so the surface never resizes. */}
      <span aria-hidden="true" className="invisible col-start-1 row-start-1">
        {COPIED_LABEL}
      </span>
      <span
        className={cn(
          "t-text-swap col-start-1 row-start-1",
          phase === "exit" && "is-exit",
          phase === "enter" && "is-enter-start"
        )}
        ref={elementRef}
      >
        {displayedText}
      </span>
    </span>
  );
}
